import React, { useState, useEffect } from 'react';
import { X, TrendingUp, TrendingDown, AlertTriangle, RefreshCw, Calculator } from 'lucide-react';
import { COLORS } from '../../../../utils/constants';
import { adminAPI } from '../../../../services/api';
import { useAdminToast } from '../../../../hooks/useAdminToast';

const EditPositionModal = ({ position, clientId, onClose, onSuccess }) => {
  const { toast } = useAdminToast();
  const [form, setForm] = useState({
    entry_price: position.entry_price?.toString() || '',
    quantity: position.quantity?.toString() || '',
    leverage: position.leverage?.toString() || '10',
    side: position.side || 'long',
    pnl_adjustment: position.pnl_adjustment?.toString() || '0',
    stop_loss: position.stop_loss?.toString() || '',
    take_profit: position.take_profit?.toString() || '',
  });
  const [loading, setLoading] = useState(false);
  const [marketPrice, setMarketPrice] = useState(0);
  const [loadingPrice, setLoadingPrice] = useState(false);

  // Fetch current market price
  useEffect(() => {
    fetchMarketPrice();
  }, [position.symbol]);

  const fetchMarketPrice = async () => {
    setLoadingPrice(true);
    try {
      const res = await adminAPI.getMarketPrices();
      const base = position.symbol?.replace('USDT', '').replace('USD', '');
      setMarketPrice(res.data?.prices?.[base] || 0);
    } catch (err) {
      console.error('Failed to fetch price');
    } finally {
      setLoadingPrice(false);
    }
  };

  // Calculated values
  const entryPrice = parseFloat(form.entry_price) || 0;
  const quantity = parseFloat(form.quantity) || 0;
  const leverage = parseInt(form.leverage) || 1;
  const pnlAdjustment = parseFloat(form.pnl_adjustment) || 0;
  
  // Position value
  const positionValue = entryPrice * quantity;
  
  // Margin
  const margin = positionValue / leverage;
  
  // Liquidation price
  const maintenanceMarginRate = 0.005;
  const liquidationPrice = form.side === 'long'
    ? entryPrice * (1 - (1 / leverage) + maintenanceMarginRate)
    : entryPrice * (1 + (1 / leverage) - maintenanceMarginRate);

  // Distance to liquidation
  const liquidationDistance = form.side === 'long'
    ? ((entryPrice - liquidationPrice) / entryPrice * 100)
    : ((liquidationPrice - entryPrice) / entryPrice * 100);

  // Current PnL based on market price
  const marketPnl = marketPrice > 0 
    ? (form.side === 'long' 
        ? (marketPrice - entryPrice) * quantity
        : (entryPrice - marketPrice) * quantity)
    : 0;

  // ROE
  const roe = margin > 0 ? (marketPnl / margin * 100) : 0;

  // SL/TP PnL
  const slPnl = form.stop_loss ? (
    form.side === 'long'
      ? (parseFloat(form.stop_loss) - entryPrice) * quantity
      : (entryPrice - parseFloat(form.stop_loss)) * quantity
  ) : null;

  const tpPnl = form.take_profit ? (
    form.side === 'long'
      ? (parseFloat(form.take_profit) - entryPrice) * quantity
      : (entryPrice - parseFloat(form.take_profit)) * quantity
  ) : null;

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const updateData = {};
      
      // Compare and collect changes
      if (form.entry_price !== position.entry_price?.toString()) {
        updateData.entry_price = parseFloat(form.entry_price);
      }
      if (form.quantity !== position.quantity?.toString()) {
        updateData.quantity = parseFloat(form.quantity);
      }
      if (form.leverage !== position.leverage?.toString()) {
        updateData.leverage = parseInt(form.leverage);
      }
      if (form.side !== position.side) {
        updateData.side = form.side;
      }
      
      // Handle pnl_adjustment - this value is ADDED to calculated PnL
      const oldAdjustment = position.pnl_adjustment?.toString() || '0';
      if (form.pnl_adjustment !== oldAdjustment) {
        updateData.pnl_adjustment = parseFloat(form.pnl_adjustment) || 0;
      }
      
      // Handle stop_loss - compare normalized values
      const oldSL = position.stop_loss != null ? position.stop_loss.toString() : '';
      const newSL = form.stop_loss || '';
      if (oldSL !== newSL) {
        updateData.stop_loss = newSL ? parseFloat(newSL) : null;
      }
      
      // Handle take_profit - compare normalized values
      const oldTP = position.take_profit != null ? position.take_profit.toString() : '';
      const newTP = form.take_profit || '';
      if (oldTP !== newTP) {
        updateData.take_profit = newTP ? parseFloat(newTP) : null;
      }

      if (Object.keys(updateData).length === 0) {
        toast.info('Нет изменений');
        setLoading(false);
        return;
      }

      await adminAPI.updatePosition(position.id, updateData);
      onSuccess();
    } catch (err) {
      console.error('Position update error:', err);
      toast.apiError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }} onClick={onClose}>
      <div style={{ backgroundColor: COLORS.card, borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {form.side === 'long' ? <TrendingUp size={20} color={COLORS.success} /> : <TrendingDown size={20} color={COLORS.danger} />}
            <div>
              <h3 style={{ color: COLORS.text, fontSize: '18px', fontWeight: '700', margin: 0 }}>
                Редактирование {position.symbol}
              </h3>
              <span style={{ 
                color: form.side === 'long' ? COLORS.success : COLORS.danger, 
                fontSize: '12px', 
                fontWeight: '600' 
              }}>
                {form.side?.toUpperCase()} {form.leverage}x
              </span>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: COLORS.textSecondary, cursor: 'pointer' }}><X size={20} /></button>
        </div>

        {/* Position Summary */}
        <div style={{ marginBottom: '16px', padding: '14px', backgroundColor: COLORS.background, borderRadius: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ color: COLORS.textSecondary, fontSize: '12px' }}>Текущая рыночная цена</span>
            <button
              onClick={fetchMarketPrice}
              disabled={loadingPrice}
              style={{ background: 'none', border: 'none', color: COLORS.primary, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px' }}
            >
              <RefreshCw size={12} className={loadingPrice ? 'animate-spin' : ''} />
              Обновить
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', textAlign: 'center' }}>
            <div style={{ padding: '10px', backgroundColor: COLORS.card, borderRadius: '8px' }}>
              <div style={{ color: COLORS.textSecondary, fontSize: '10px', marginBottom: '2px' }}>Рыночная цена</div>
              <div style={{ color: COLORS.primary, fontSize: '16px', fontWeight: '700' }}>${marketPrice.toFixed(2)}</div>
            </div>
            <div style={{ padding: '10px', backgroundColor: COLORS.card, borderRadius: '8px' }}>
              <div style={{ color: COLORS.textSecondary, fontSize: '10px', marginBottom: '2px' }}>PnL по рынку</div>
              <div style={{ color: marketPnl >= 0 ? COLORS.success : COLORS.danger, fontSize: '16px', fontWeight: '700' }}>
                {marketPnl >= 0 ? '+' : ''}${marketPnl.toFixed(2)}
              </div>
            </div>
            <div style={{ padding: '10px', backgroundColor: COLORS.card, borderRadius: '8px' }}>
              <div style={{ color: COLORS.textSecondary, fontSize: '10px', marginBottom: '2px' }}>ROE</div>
              <div style={{ color: roe >= 0 ? COLORS.success : COLORS.danger, fontSize: '16px', fontWeight: '700' }}>
                {roe >= 0 ? '+' : ''}{roe.toFixed(2)}%
              </div>
            </div>
          </div>
        </div>

        {/* Position Parameters */}
        <div style={{ marginBottom: '16px', padding: '14px', backgroundColor: COLORS.background, borderRadius: '10px' }}>
          <div style={{ color: COLORS.text, fontSize: '13px', fontWeight: '600', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Calculator size={14} /> Параметры позиции
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: COLORS.textSecondary, fontSize: '10px', marginBottom: '2px' }}>Размер</div>
              <div style={{ color: COLORS.text, fontSize: '13px', fontWeight: '600' }}>${positionValue.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: COLORS.textSecondary, fontSize: '10px', marginBottom: '2px' }}>Маржа</div>
              <div style={{ color: COLORS.primary, fontSize: '13px', fontWeight: '600' }}>${margin.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: COLORS.textSecondary, fontSize: '10px', marginBottom: '2px' }}>Ликвидация</div>
              <div style={{ color: COLORS.danger, fontSize: '13px', fontWeight: '600' }}>${liquidationPrice.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: COLORS.textSecondary, fontSize: '10px', marginBottom: '2px' }}>До ликвид.</div>
              <div style={{ color: COLORS.danger, fontSize: '13px', fontWeight: '600' }}>-{liquidationDistance.toFixed(2)}%</div>
            </div>
          </div>
        </div>

        {/* Liquidation Warning */}
        {liquidationDistance < 20 && (
          <div style={{
            padding: '10px 12px',
            backgroundColor: `${COLORS.danger}15`,
            borderRadius: '8px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            border: `1px solid ${COLORS.danger}30`,
          }}>
            <AlertTriangle size={16} color={COLORS.danger} />
            <span style={{ color: COLORS.danger, fontSize: '12px' }}>
              Внимание! До ликвидации осталось менее {liquidationDistance.toFixed(1)}%
            </span>
          </div>
        )}

        {/* Edit Form */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          {/* Entry Price */}
          <div>
            <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '12px', marginBottom: '4px' }}>Цена входа ($)</label>
            <input
              type="number"
              value={form.entry_price}
              onChange={(e) => setForm({ ...form, entry_price: e.target.value })}
              style={{ width: '100%', padding: '10px', backgroundColor: COLORS.background, border: `1px solid ${COLORS.border}`, borderRadius: '8px', color: COLORS.text, fontSize: '14px', boxSizing: 'border-box' }}
            />
          </div>

          {/* Quantity */}
          <div>
            <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '12px', marginBottom: '4px' }}>Объём</label>
            <input
              type="number"
              value={form.quantity}
              onChange={(e) => setForm({ ...form, quantity: e.target.value })}
              style={{ width: '100%', padding: '10px', backgroundColor: COLORS.background, border: `1px solid ${COLORS.border}`, borderRadius: '8px', color: COLORS.text, fontSize: '14px', boxSizing: 'border-box' }}
            />
          </div>

          {/* Leverage */}
          <div>
            <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '12px', marginBottom: '4px' }}>Плечо</label>
            <div style={{ display: 'flex', gap: '4px' }}>
              {[5, 10, 20, 50, 100].map(l => (
                <button
                  key={l}
                  onClick={() => setForm({ ...form, leverage: l.toString() })}
                  style={{
                    flex: 1, padding: '10px 4px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                    backgroundColor: parseInt(form.leverage) === l ? COLORS.primary : COLORS.background,
                    color: parseInt(form.leverage) === l ? '#000' : COLORS.textSecondary,
                    fontSize: '11px', fontWeight: '600'
                  }}
                >
                  {l}x
                </button>
              ))}
            </div>
          </div>

          {/* Side */}
          <div>
            <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '12px', marginBottom: '4px' }}>Направление</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setForm({ ...form, side: 'long' })}
                style={{
                  flex: 1, padding: '10px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                  backgroundColor: form.side === 'long' ? COLORS.success : COLORS.background,
                  color: form.side === 'long' ? '#fff' : COLORS.textSecondary,
                  fontWeight: '600', fontSize: '12px'
                }}
              >
                LONG
              </button>
              <button
                onClick={() => setForm({ ...form, side: 'short' })}
                style={{
                  flex: 1, padding: '10px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                  backgroundColor: form.side === 'short' ? COLORS.danger : COLORS.background,
                  color: form.side === 'short' ? '#fff' : COLORS.textSecondary,
                  fontWeight: '600', fontSize: '12px'
                }}
              >
                SHORT
              </button>
            </div>
          </div>

          {/* PnL Adjustment */}
          <div style={{ gridColumn: '1 / -1' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
              <label style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
                Корректировка PnL ($)
                <span style={{ color: COLORS.textSecondary, fontSize: '10px', marginLeft: '4px' }}>
                  (добавляется к расчётному)
                </span>
              </label>
              <span style={{ color: COLORS.textSecondary, fontSize: '11px' }}>
                Расчётный: {marketPnl >= 0 ? '+' : ''}{marketPnl.toFixed(2)} | Итого: {(marketPnl + pnlAdjustment) >= 0 ? '+' : ''}{(marketPnl + pnlAdjustment).toFixed(2)}
              </span>
            </div>
            <input
              type="number"
              value={form.pnl_adjustment}
              onChange={(e) => setForm({ ...form, pnl_adjustment: e.target.value })}
              style={{ 
                width: '100%', padding: '10px', 
                backgroundColor: COLORS.background, 
                border: `1px solid ${pnlAdjustment >= 0 ? COLORS.success : COLORS.danger}`, 
                borderRadius: '8px', 
                color: pnlAdjustment >= 0 ? COLORS.success : COLORS.danger, 
                fontSize: '16px', 
                fontWeight: '600',
                boxSizing: 'border-box' 
              }}
            />
            <div style={{ marginTop: '4px', display: 'flex', gap: '4px' }}>
              {[-100, -50, 0, 50, 100, 200].map(val => (
                <button
                  key={val}
                  onClick={() => setForm({ ...form, pnl_adjustment: val.toString() })}
                  style={{
                    flex: 1, padding: '4px', backgroundColor: COLORS.background,
                    border: `1px solid ${COLORS.border}`, borderRadius: '4px',
                    color: val === 0 ? COLORS.textSecondary : val > 0 ? COLORS.success : COLORS.danger,
                    fontSize: '10px', cursor: 'pointer'
                  }}
                >
                  {val > 0 ? '+' : ''}{val}
                </button>
              ))}
            </div>
          </div>

          {/* Stop Loss */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
              <label style={{ color: COLORS.textSecondary, fontSize: '12px' }}>Stop Loss ($)</label>
              {slPnl !== null && (
                <span style={{ color: slPnl >= 0 ? COLORS.success : COLORS.danger, fontSize: '10px' }}>
                  PnL: {slPnl >= 0 ? '+' : ''}{slPnl.toFixed(2)}
                </span>
              )}
            </div>
            <input
              type="number"
              value={form.stop_loss}
              onChange={(e) => setForm({ ...form, stop_loss: e.target.value })}
              placeholder={form.side === 'long' ? `< ${entryPrice.toFixed(0)}` : `> ${entryPrice.toFixed(0)}`}
              style={{ width: '100%', padding: '10px', backgroundColor: COLORS.background, border: `1px solid ${COLORS.border}`, borderRadius: '8px', color: COLORS.text, fontSize: '14px', boxSizing: 'border-box' }}
            />
          </div>

          {/* Take Profit */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
              <label style={{ color: COLORS.textSecondary, fontSize: '12px' }}>Take Profit ($)</label>
              {tpPnl !== null && (
                <span style={{ color: tpPnl >= 0 ? COLORS.success : COLORS.danger, fontSize: '10px' }}>
                  PnL: {tpPnl >= 0 ? '+' : ''}{tpPnl.toFixed(2)}
                </span>
              )}
            </div>
            <input
              type="number"
              value={form.take_profit}
              onChange={(e) => setForm({ ...form, take_profit: e.target.value })}
              placeholder={form.side === 'long' ? `> ${entryPrice.toFixed(0)}` : `< ${entryPrice.toFixed(0)}`}
              style={{ width: '100%', padding: '10px', backgroundColor: COLORS.background, border: `1px solid ${COLORS.border}`, borderRadius: '8px', color: COLORS.text, fontSize: '14px', boxSizing: 'border-box' }}
            />
          </div>
        </div>

        {/* Submit Button */}
        <button 
          onClick={handleSubmit} 
          disabled={loading} 
          style={{ 
            width: '100%', 
            marginTop: '20px', 
            padding: '14px', 
            backgroundColor: loading ? COLORS.border : COLORS.primary, 
            border: 'none', 
            borderRadius: '8px', 
            color: '#000', 
            fontSize: '15px', 
            fontWeight: '600', 
            cursor: loading ? 'not-allowed' : 'pointer' 
          }}
        >
          {loading ? 'Сохранение...' : 'Сохранить изменения'}
        </button>
      </div>
    </div>
  );
};

export default EditPositionModal;
